import { useState } from "react";
import { Hexagon, X, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* Beat editor — name, color, shift and unit assignment for a map zone drawn
   by command. Create mode receives the dragged rect; edit mode receives the beat. */

const COLORS = ["#38bdf8", "#34d399", "#fb923c", "#f472b6", "#facc15", "#a78bfa"];
const SHIFTS = ["All shifts", "Day", "Night", "Swing"];

export default function BeatForm({ serverId, token, demo, rect, beat, mapUnits, onDone, onCancel, onDelete }) {
  const [name, setName] = useState(beat?.name || "");
  const [color, setColor] = useState(beat?.color || COLORS[0]);
  const [shift, setShift] = useState(beat?.shift || SHIFTS[0]);
  const [units, setUnits] = useState(beat?.units || []);
  const [busy, setBusy] = useState(false);

  const toggleUnit = (cs) => setUnits((u) => u.includes(cs) ? u.filter((x) => x !== cs) : [...u, cs]);

  const save = async () => {
    if (name.trim().length < 2) return;
    setBusy(true);
    const body = { name: name.trim(), color, shift, units,
      x1: beat ? beat.zone.x1 : rect.x1, y1: beat ? beat.zone.y1 : rect.y1,
      x2: beat ? beat.zone.x2 : rect.x2, y2: beat ? beat.zone.y2 : rect.y2 };
    if (demo) { onDone({ ...body, id: beat?.id || `l${Date.now()}`, zone: { x1: body.x1, y1: body.y1, x2: body.x2, y2: body.y2 }, name: body.name.toUpperCase() }); return; }
    try {
      const r = beat
        ? await api.put(`/nc/servers/${serverId}/cad/beats/${beat.id}`, body, { params: { token } })
        : await api.post(`/nc/servers/${serverId}/cad/beats`, body, { params: { token } });
      toastOk(r.data.message);
      onDone(r.data.beat);
    } catch (e) {
      import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed"));
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="beat-modal">
      <div className="w-full max-w-sm border border-white/15 bg-[#0a0d12] p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Hexagon className="w-4 h-4" style={{ color }} />
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-100">{beat ? "Edit beat" : "New patrol beat"}</p>
          <button data-testid="beat-cancel-x" onClick={onCancel} className="ml-auto text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
        </div>
        <input data-testid="beat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Beat name (e.g. CRESTWOOD)" className={inputCls} />
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-[8px] uppercase text-zinc-500">Color</span>
          {COLORS.map((c) => (
            <button key={c} data-testid={`beat-color-${c.slice(1)}`} onClick={() => setColor(c)}
              className={`w-4 h-4 rounded-full border ${color === c ? "border-white" : "border-transparent"}`} style={{ backgroundColor: c }} />
          ))}
          <select data-testid="beat-shift" value={shift} onChange={(e) => setShift(e.target.value)} className={`${inputCls} ml-auto`}>
            {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Assign units (on duty)</p>
          <div className="flex flex-wrap gap-1.5">
            {(mapUnits || []).map((u) => (
              <button key={u.callsign} data-testid={`beat-unit-${u.callsign}`} onClick={() => toggleUnit(u.callsign)}
                className={`${btnCls} ${units.includes(u.callsign) ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>
                {u.callsign}
              </button>
            ))}
            {(mapUnits || []).length === 0 && <p className="font-mono text-[8px] text-zinc-600 uppercase">No units reporting positions</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="beat-save" onClick={save} disabled={busy || name.trim().length < 2}
            className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1`}>
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} {beat ? "Save beat" : "Post beat"}
          </button>
          {beat && onDelete && (
            <button data-testid="beat-delete" onClick={onDelete} className={`${btnCls} border-red-500/50 text-red-300 hover:bg-red-500/10 flex items-center gap-1`}><Trash2 className="w-3 h-3" /> Wipe</button>
          )}
          <button data-testid="beat-cancel" onClick={onCancel} className={`${btnCls} border-white/15 text-zinc-400`}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
